import React, { useCallback, useMemo } from "react";

import { faAngleDown } from "@fortawesome/pro-solid-svg-icons";
import { faExclamationTriangle } from "@fortawesome/pro-solid-svg-icons/faExclamationTriangle";
import { faInfoCircle } from "@fortawesome/pro-solid-svg-icons/faInfoCircle";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Tag from "antd/es/tag";
import Tooltip from "antd/es/tooltip";
import cn from "classnames";
import { getHours, getMinutes, isBefore, isSameDay, set, startOfDay } from "date-fns";
import { useIntl } from "react-intl";

import EmailQuota from "@mapmycustomers/shared/types/email/EmailQuota";
import { Calendar, TimeDropdownPicker } from "@mapmycustomers/ui";

import { SHOULD_USE_12_HOUR_FORMAT } from "@app/util/consts";

import messages from "../../messages";
import EmailSendingWindow from "../../types/EmailSendingWindow";
import createDisabledTime from "../../utils/createDisabledTime";
import { roundUpTo15Min } from "../../utils/getDefaultTodayTime";

import styles from "./CustomDateView.module.scss";

interface CustomDateViewProps {
  emailQuota?: EmailQuota;
  excludedDayIndexes?: number[];
  onChange: (dateTime: Date) => void;
  sendingWindow: EmailSendingWindow;
  sendingWindowLabel?: string;
  value?: Date;
}

const CustomDateView = ({
  emailQuota,
  excludedDayIndexes,
  onChange,
  sendingWindow,
  sendingWindowLabel,
  value,
}: CustomDateViewProps) => {
  const intl = useIntl();

  const disabledDate = useMemo(() => {
    const today = startOfDay(new Date());
    return (current: Date) =>
      isBefore(current, today) || !!excludedDayIndexes?.includes(current.getDay());
  }, [excludedDayIndexes]);

  const handleDateChange = useCallback(
    (date: Date | undefined) => {
      if (date) {
        onChange(
          value
            ? set(date, { hours: getHours(value), minutes: getMinutes(value) })
            : set(date, { hours: 8, minutes: 0 })
        );
      }
    },
    [onChange, value]
  );

  const handleTimeChange = useCallback(
    (time: Date | null) => {
      if (time && value) {
        onChange(set(value, { hours: getHours(time), minutes: getMinutes(time) }));
      }
    },
    [onChange, value]
  );

  const disabledTime = useMemo(
    () =>
      createDisabledTime(
        sendingWindow,
        value && isSameDay(value, new Date()) ? roundUpTo15Min(new Date()) : undefined
      ),
    [sendingWindow, value]
  );

  const reserved = emailQuota ? emailQuota.total - emailQuota.remaining : 0;
  const isLimitReached = emailQuota ? emailQuota.remaining <= 0 : false;
  const showQuotaTag = emailQuota && reserved > emailQuota.total * 0.9;

  return (
    <div className={styles.container}>
      <Calendar
        disabledDate={disabledDate}
        fullscreen={false}
        headerDateFormat="MMM yyyy"
        onChange={handleDateChange}
        value={value}
      />
      <div className={styles.sendTimeSection}>
        <div className={styles.sendTimeLabel}>
          <span>
            {intl.formatMessage({
              id: "email.emailScheduleSendPopover.sendTime",
              defaultMessage: "Send Time",
              description: "Schedule send custom date view time label",
            })}
          </span>
          {sendingWindowLabel && (
            <Tooltip
              overlayClassName={styles.tooltip}
              placement="topLeft"
              title={sendingWindowLabel}
            >
              <FontAwesomeIcon className={styles.infoIcon} icon={faInfoCircle} />
            </Tooltip>
          )}
        </div>
        <TimeDropdownPicker
          className={styles.timePicker}
          disabledTime={disabledTime}
          onChange={handleTimeChange}
          stepMinutes={15}
          suffixIcon={<FontAwesomeIcon className={styles.arrowIcon} icon={faAngleDown} />}
          timeFormat={SHOULD_USE_12_HOUR_FORMAT ? "hh:mm a" : "HH:mm"}
          use12Hours={SHOULD_USE_12_HOUR_FORMAT}
          value={value}
        />
      </div>
      {showQuotaTag && emailQuota && (
        <Tag
          className={cn(styles.quotaTag, {
            [styles.quotaTagError]: isLimitReached,
          })}
          icon={
            <FontAwesomeIcon
              className={isLimitReached ? styles.quotaIconError : styles.quotaIconWarning}
              icon={faExclamationTriangle}
            />
          }
        >
          {intl.formatMessage(
            !isLimitReached ? messages.quotaSlotsReserved : messages.quotaLimitReached,
            {
              remaining: emailQuota.remaining,
              reserved,
              total: emailQuota.total,
            }
          )}
        </Tag>
      )}
    </div>
  );
};

export default CustomDateView;
